<script>
  import { getContext, createEventDispatcher, onDestroy } from "svelte";
  import isEmpty from "lodash/isEmpty";
  import TextInput from "#lib/sidebar/TextInput.svelte";
  import SelectInput from "#lib/sidebar/SelectInput.svelte";
  import { discontiguousKey } from "#src/extensions/event-keys.js";
  import { settingTriopsUrl as triopsUrl } from "#src/stores/ui.js";
  import SpinnerIcon from "@local/assets/icons/spinner.svg";
  import { api } from "#src/api";
  import extension from "@local/extensions/utilities/extension.js";
  import { pollUntilComplete } from "#src/extensions/check-document-status.js";
  import { prioritizePages } from "#src/api";

  export let group;
  export let documents;
  export let newDoc;
  export let action = "add"; // "add" | "update"

  let selectedPages = {};
  let lastToggle = null;
  let controller = new AbortController();
  let sourceDocId;

  const fileData = getContext("fileData");
  const dispatch = createEventDispatcher();

  $: checkSourceDoc(newDoc);

  function checkSourceDoc(workingDoc) {
    if (sourceDocId !== workingDoc.source_document_id && workingDoc.source_document_id) {
      controller.abort();
      controller = new AbortController();
      sourceDocId = workingDoc.source_document_id;
      const getDoc = () => $group.documents[sourceDocId];
      const updateDoc = (update) => {
        if (update) {
          group.updateDocumentNotUndoable(update.id, update.diff);
        }
      };
      pollUntilComplete(fileData, getDoc, updateDoc, controller.signal);
    }
  }

  async function pageSrc(page, $fileData) {
    const fd = $fileData[page.name];
    if (!fd) return "";
    const isPdf = fd.file.metadata.mimetype === "application/pdf";

    if (isPdf && fd.status[page.page]?.state === "completed") {
      const docid = page.name.split("/")[0];
      const path = encodeURIComponent(`${docid}/doc.${page.page}.tiff`);
      return `${$triopsUrl}/iiif/2/drawings/${path}/full/250,/0/default.jpg`;
    } else if (!isPdf) {
      const { data, error } = await api.storage.from("documents").createSignedUrl(page.name, 60 * 60, {
        transform: { width: 250, height: 167, resize: "contain" },
      });
      if (error) return "";
      return data.signedUrl;
    }

    return "";
  }

  function pageLabel(page) {
    if (page.label) return page.label;
    const fd = $fileData[page.name];
    if (fd) {
      const sheet = fd.sheets[page.page];
      if (sheet?.name) return sheet.name;
    }
    return `Page ${page.page + 1}`;
  }

  function togglePage(id, shiftKey = false, disKey = false) {
    if (shiftKey && lastToggle) {
      const doc = $group.documents[newDoc.source_document_id];
      if (doc) {
        const index = doc.data.pages.indices[id];
        const prevIndex = doc.data.pages.indices[lastToggle];
        const [start, end] = index < prevIndex ? [index, prevIndex] : [prevIndex, index];
        const ids = doc.data.pages.order.slice(start, end + 1);
        ids.forEach((id) => (selectedPages[id] = true));
      }
    } else if (disKey) {
      if (!selectedPages[id]) {
        selectedPages[id] = true;
        lastToggle = id;
      } else {
        delete selectedPages[id];
        selectedPages = selectedPages;
      }
    } else {
      selectedPages = { [id]: true };
      lastToggle = id;
    }
  }

  function defaultFilters() {
    return {
      show_items: true,
      show_collections: true,
      show_types: true,
      show_notes: true,
      show_dimensions: true,
      location_filter: null,
      item_filter: null,
      collection_filter: null,
      type_filter: null,
      note_filter: null,
      hidden_pages: {},
      hidden_locations: {},
    };
  }

  function addVirtualDocument() {
    if (!newDoc.source_document_id) return;
    const selected = Object.keys(selectedPages);
    const source = $group.documents[newDoc.source_document_id];
    if (!source) return;

    const firstPage = selected.length
      ? source.data.pages.order.find((id) => selectedPages[id])
      : source.data.pages.order[0];
    const page = source.data.pages[firstPage];
    const ext = extension(page.name);
    let thumbnail;
    let filters = defaultFilters();
    if (ext === "pdf") {
      const id = page.name.split("/")[0];
      thumbnail = {
        bucket: "drawings",
        name: page.label || "",
        objectName: page.name,
        path: `${id}/doc.${page.page}.tiff`,
        type: "iiif",
      };
    } else {
      thumbnail = {
        bucket: "documents",
        path: page.name,
        type: "image",
        name: page.label || "",
      };
    }

    if (selected.length > 0) {
      source.data.pages.order.forEach((id) => {
        if (!selectedPages[id]) {
          filters.hidden_pages[id] = true;
        }
      });

      const prioritize = {};
      selected.forEach((id) => {
        const page = source.data.pages[id];
        if (!prioritize[page.name]) {
          prioritize[page.name] = [];
        }
        prioritize[page.name].push(page.page);
      });

      const prioritizeRequests = Object.keys(prioritize).map((name) =>
        prioritizePages({ pages: prioritize[name], name, priority: 2 }),
      );
      Promise.all(prioritizeRequests);
    }

    if (action === "add") {
      newDoc.data.thumbnail = thumbnail;
      newDoc.data.filters = filters;
      group.addDocument(newDoc);
    } else if (action === "update") {
      group.updateDocument(newDoc.id, {
        name: newDoc.name,
        data: {
          pages: null,
          thumbnail,
          filters,
        },
      });
    }

    selectedPages = {};
    lastToggle = null;
    dispatch("done-uploading", { id: newDoc.id });
  }

  onDestroy(() => {
    controller.abort();
  });
</script>

<div class="flex-none flex flex-col overflow-hidden gap-4">
  <div class="text-center text-sm font-bold">Create New Working Document</div>
  <TextInput label="Name" labelWidth="8rem" bind:value={newDoc.name} border inputOnKeydown />
  {#if action === "add"}
    <SelectInput
      label="Source Document"
      labelWidth="8rem"
      border
      bind:value={newDoc.source_document_id}
      on:input={() => {
        selectedPages = {};
        lastToggle = null;
      }}
      options={documents
        .filter((doc) => !doc.is_virtual)
        .map((doc) => ({ label: doc.name, value: doc.id }))} />
  {/if}
</div>
{#if newDoc.source_document_id && $group.documents[newDoc.source_document_id]}
  {@const doc = $group.documents[newDoc.source_document_id]}
  {@const pending = doc.data.pages.order.some((id) => doc.data.pages[id].pending)}
  <div class="grow flex flex-col gap-2 overflow-hidden">
    <div class="px-2 flex-none flex justify-between">
      <div>Select pages (can be modified later):</div>
      <div class="font-bold">
        {Object.keys(selectedPages).length} / {doc.data.pages.order.length} selected
      </div>
    </div>
    {#if doc.data.pages.order.length > 50 && (isEmpty(selectedPages) || Object.keys(selectedPages).length > 50)}
      <div class="text-red-500 px-2">
        Documents of more than 50 pages may be difficult to navigate; consider selecting a smaller subset of
        pages.
      </div>
    {/if}
    <div class="w-full grow overflow-hidden border rounded overflow-y-auto border-gray-300">
      <div class="w-full grid grid-cols-3 gap-1 p-2">
        {#if pending}
          <div class="col-span-3 h-32 flex items-center justify-center">
            <div class="flex items-center gap-2">
              <div class="animate-spin text-gray-500">
                <SpinnerIcon />
              </div>
              <div class="text-gray-500 italic">Loading pages...</div>
            </div>
          </div>
        {:else}
          {#each doc.data.pages.order as pageId, index}
            {@const page = doc.data.pages[pageId]}
            {@const label = pageLabel(page)}
            {#await pageSrc(page, $fileData)}
              <button
                class="h-32 flex gap-2 items-start overflow-x-hidden border p-2 outline-none"
                class:border-transparent={!selectedPages[pageId]}
                class:border-blue-500={selectedPages[pageId]}
                on:mousedown={(e) => togglePage(pageId, e.shiftKey, discontiguousKey(e))}>
                <div class="flex-none">{index + 1}</div>
                <div
                  class="h-full flex-grow bg-gray-100 border border-gray-300 rounded overflow-hidden relative text-xs">
                  <div class="h-full w-full flex items-center justify-center">
                    <div class="animate-spin relative text-gray-500">
                      <SpinnerIcon />
                    </div>
                  </div>
                  <div
                    class="absolute bottom-0 left-0 border border-transparent truncate text-xxs text-left px-1 bg-white/50 rounded">
                    {label}
                  </div>
                </div>
              </button>
            {:then src}
              <button
                class="h-32 flex gap-2 items-start overflow-x-hidden border p-2 outline-none"
                class:border-transparent={!selectedPages[pageId]}
                class:border-blue-500={selectedPages[pageId]}
                on:mousedown={(e) => togglePage(pageId, e.shiftKey, discontiguousKey(e))}>
                <div class="flex-none">{index + 1}</div>
                <div
                  class="h-full flex-grow bg-gray-100 border border-gray-300 rounded overflow-hidden relative text-xs">
                  {#if !src}
                    <div class="h-full w-full flex items-center justify-center">
                      <div class="animate-spin relative text-gray-500">
                        <SpinnerIcon />
                      </div>
                    </div>
                  {:else}
                    <img class="h-full w-full object-contain" {src} alt={page.label} draggable={false} />
                  {/if}
                  <div
                    class="absolute bottom-0 left-0 border border-transparent truncate text-xxs text-left px-1 bg-white/50 rounded">
                    {label}
                  </div>
                </div>
              </button>
            {/await}
          {/each}
        {/if}
      </div>
    </div>
  </div>

  <button
    class="btn btn-primary w-full"
    disabled={!newDoc.source_document_id || pending}
    on:click={addVirtualDocument}>
    {#if action === "add"}
      Create Working Document
    {:else if action === "update"}
      Go to Document
    {/if}
  </button>
{/if}
